import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import CommonModal from '../common/CommonModal';
import ResizableTable from '../common/ResizableTable';
import { prepareTableData, getFilteredAndSortedRecords } from './utils/listViewUtils';
import { fetchSearchWidgetListRecords } from './hooks/useSearchViewWidgetData';
import {
  normalizeAllowedRelatedListActions,
  buildRelatedListRowActions,
  buildRelatedListHeaderActions
} from '../../utils/buildRelatedListTableActions';

/**
 * Same shell as RelatedListView (CommonModal + RELATED RECORDS subtitle): full list for a search widget
 * using the same object/list filters as the embedded table (limit 0 = all).
 */
const SearchViewShowAllModal = ({
  show,
  onHide,
  title = 'Records',
  widget,
  recordData,
  searchViewConfig,
  fieldDataTypes = {},
  fieldMetadata = {},
  searchObjectName,
  currentRecordId,
  sourceObjectName
}) => {
  const [rawRecords, setRawRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedRows, setSelectedRows] = useState([]);
  const abortRef = useRef(null);

  const widgetConfig = widget?.config || {};
  const filterBindings = widgetConfig.filter_bindings || [];
  const selectedFields = widgetConfig.fields || [];

  const allowedActions = useMemo(
    () => normalizeAllowedRelatedListActions(widgetConfig.actions, searchObjectName),
    [widgetConfig.actions, searchObjectName]
  );

  const rowActions = useMemo(
    () => buildRelatedListRowActions(allowedActions, { objectName: searchObjectName }),
    [allowedActions, searchObjectName]
  );

  const headerActions = useMemo(
    () =>
      buildRelatedListHeaderActions(allowedActions, {
        isAttachmentTab: false,
        isHistoryObject: false,
        selectedCount: selectedRows.length
      }),
    [allowedActions, selectedRows.length]
  );

  const displayFieldsArray = useMemo(() => {
    if (!searchViewConfig?.display_fields) return [];
    if (Array.isArray(searchViewConfig.display_fields)) {
      return searchViewConfig.display_fields;
    }
    if (typeof searchViewConfig.display_fields === 'string') {
      try {
        return JSON.parse(searchViewConfig.display_fields);
      } catch {
        return searchViewConfig.display_fields.split(',').map((f) => f.trim()).filter(Boolean);
      }
    }
    return [];
  }, [searchViewConfig]);

  const columnFieldNames = useMemo(() => {
    const base = selectedFields.length > 0 ? selectedFields : displayFieldsArray;
    return base.filter((f) => f && f !== 'id');
  }, [selectedFields, displayFieldsArray]);

  const columns = useMemo(() => {
    return columnFieldNames.map((fieldName) => {
      const meta = fieldMetadata[fieldName] || {};
      const dataType = (fieldDataTypes[fieldName] || 'text').toLowerCase();
      const isNumeric = ['currency', 'number', 'decimal', 'integer', 'rollupsummary'].includes(
        dataType
      );
      return {
        field: fieldName,
        label: meta.field_label || fieldName,
        sortable: true,
        dataType,
        align: isNumeric ? 'right' : 'left'
      };
    });
  }, [columnFieldNames, fieldMetadata, fieldDataTypes]);

  const tableData = useMemo(() => {
    if (!searchObjectName) return [];
    return prepareTableData(rawRecords || [], searchObjectName, fieldDataTypes);
  }, [rawRecords, searchObjectName, fieldDataTypes]);

  const filteredSorted = useMemo(
    () => getFilteredAndSortedRecords(tableData, searchTerm, {}, sortBy, sortOrder),
    [tableData, searchTerm, sortBy, sortOrder]
  );

  const recordLinkField = useMemo(() => {
    if (columnFieldNames.map((f) => f.toLowerCase()).includes('name')) {
      return 'name';
    }
    return columnFieldNames[0] || 'name';
  }, [columnFieldNames]);

  const loadRecords = useCallback(async () => {
    if (!show || !searchViewConfig || !searchObjectName || !recordData) return;
    if (abortRef.current) {
      abortRef.current.abort();
    }
    const ac = new AbortController();
    abortRef.current = ac;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchSearchWidgetListRecords({
        searchViewConfig,
        searchObjectName,
        recordData,
        filterBindings,
        selectedFields,
        limit: 0,
        signal: ac.signal
      });
      if (ac.signal.aborted) return;
      if (result.success) {
        setRawRecords(result.records || []);
      } else {
        setError(result.message || 'Failed to load records');
        setRawRecords([]);
      }
    } catch (e) {
      if (e.name === 'AbortError' || e.name === 'CanceledError') return;
      setError(e.message || 'Failed to load records');
      setRawRecords([]);
    } finally {
      if (!ac.signal.aborted) {
        setLoading(false);
      }
    }
  }, [
    show,
    searchViewConfig,
    searchObjectName,
    recordData,
    filterBindings,
    selectedFields
  ]);

  useEffect(() => {
    if (show) {
      loadRecords();
    } else {
      setRawRecords([]);
      setSearchTerm('');
      setSelectedRows([]);
      setError(null);
    }
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [show, loadRecords]);

  const handleSort = useCallback(
    (field) => {
      if (sortBy === field) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(field);
        setSortOrder('asc');
      }
    },
    [sortBy]
  );

  const refresh = useCallback(() => {
    loadRecords();
  }, [loadRecords]);

  const shouldShowActions = rowActions.length > 0;

  if (!show) return null;

  return (
    <CommonModal
      show={show}
      onHide={onHide}
      size="xl"
      className="related-list-view search-view-show-all-modal"
      title={title}
      subtitle="RELATED RECORDS"
      headerIcon={<i className="fas fa-list"></i>}
      backdrop="static"
      keyboard={true}
      showCloseButton={true}
      closeButtonText="Close"
      closeButtonVariant="secondary"
    >
      <ResizableTable
        data={filteredSorted}
        columns={columns}
        loading={loading}
        error={error}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        currentPage={1}
        totalRecords={filteredSorted.length}
        recordsPerPage={filteredSorted.length}
        onPageChange={() => {}}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        showSerialNumber={true}
        showActions={shouldShowActions}
        actions={rowActions}
        headerActions={headerActions}
        selectedRows={selectedRows}
        onRowSelectionChange={setSelectedRows}
        exportFilename={`${searchObjectName || 'search'}_widget_all`}
        exportSheetName={widget?.widgetLabel || 'search'}
        className="related-list-view-table"
        tableClassName="table-theme"
        emptyMessage="No records found"
        showSearch={true}
        showPagination={false}
        searchPlaceholder="Search records..."
        objectName={searchObjectName}
        sourcePage="relatedlist"
        onRefresh={refresh}
        recordId={currentRecordId}
        targetField={null}
        parentObjectName={sourceObjectName}
        recordLinkField={recordLinkField}
        allObjectFields={[]}
        headerActionsUnifiedDropdown={true}
      />
    </CommonModal>
  );
};

export default SearchViewShowAllModal;
